"use client";
import React, { useState, useRef, useEffect } from "react";
import {
  MoreHorizontal,
  Trash2,
  Flag,
  Edit2,
  Smile,
  Image as ImageIcon,
  X,
  Hash,
  AtSign,
  Link as LinkIcon,
  MapPin,
  AlertTriangle,
  HandHeart,
  ChevronDown,
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuRadioGroup,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useMutation } from "@apollo/client";
import {
  ADD_REPORT,
  ADD_TAG,
  UPDATE_POST,
} from "@/api/apollo-client/mutations/mutations";
import { useAuth } from "@/contexts/authContext";
import toast from "react-hot-toast";
import { FormattedPost, Post } from "@/types";
import { useTextInput } from "@/hooks/Pagination/useTextInput";
import { useEmojiPicker } from "@/hooks/useEmojiPicker";
import { useMediaUpload } from "@/hooks/useMediaUpload";
import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import { PostType } from "@/constants";
import { useTheme } from "next-themes";
import AssociationWith, { Mentions } from "../AssociationWith";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import Image from "next/image";
import TextareaAutosize from "react-textarea-autosize";
import SuggestionSearch from "../SuggestionSearch";
import { parsePostText } from "@/utils/parsePostText";
import LinkPreview from "../LinkPreview";
import { sendClickEvent } from "../Analytics";
import { useRouter } from "next/navigation";

interface PostMedia {
  id: string;
  url: string;
  type: string;
  processing_state: string;
}

// Helper to parse backend text to plain text and extract tags/mentions
function parseBackendTextToPlain(text: string) {
  let plain = text.replace(/#\[(.*?)\]\(tag:.*?\)/g, "#$1");
  plain = plain.replace(/@\[(.*?)\]\(user:.*?\)/g, "@$1");
  return plain;
}

function extractTagsFromBackendText(text: string) {
  const tagRegex = /#\[(.*?)\]\(tag:(.*?)\)/g;
  const tags = [];
  let match;
  while ((match = tagRegex.exec(text)) !== null) {
    tags.push({ id: match[2], value: match[1] });
  }
  return tags;
}

function extractMentionsFromBackendText(text: string) {
  const mentionRegex = /@\[(.*?)\]\(user:(.*?)\)/g;
  const mentions = [];
  let match;
  while ((match = mentionRegex.exec(text)) !== null) {
    mentions.push({ id: match[2], value: match[1] });
  }
  return mentions;
}

interface MediaPreview {
  type: string;
  url: string;
  post: FormattedPost;
  onPostUpdate?: () => void;
}

interface PostMenuProps {
  postId: string | number;
  isAuthor: boolean;
  post: FormattedPost;
  onPostUpdate?: () => void;
}

const PostMenu: React.FC<PostMenuProps> = ({
  postId,
  isAuthor,
  post,
  onPostUpdate,
}) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const router = useRouter();

  // Only create mutations if user is authenticated
  const [updatePost] = useMutation(UPDATE_POST);
  const [addTagMutation] = useMutation(ADD_TAG);
  const [addReport] = useMutation(ADD_REPORT);

  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [existingMedia, setExistingMedia] = useState<PostMedia[]>([]);
  const [postType, setPostType] = useState<PostType>(
    post.type || PostType.GENERAL
  );
  const [selected, setSelected] = useState<Mentions[]>([]);

  // Use our custom hooks for text input and emoji picker
  const {
    text,
    setText,
    backendText,
    setBackendText,
    charCount,
    links,
    tags,
    mentions,
    selectedTags,
    setSelectedTags,
    selectedMentions,
    setSelectedMentions,
    rebuildBackendText,
  } = useTextInput();

  const { showEmojiPicker, setShowEmojiPicker, pickerRef, handleEmojiSelect } =
    useEmojiPicker();

  const {
    media,
    mediaPreview,
    fileInputRef,
    handleMediaUpload,
    removeMedia,
    uploadMedia,
  } = useMediaUpload({ userId: user?.id });

  // Initialize text input with comment content (plain text) and tags/mentions from backendText
  useEffect(() => {
    if (isEditDialogOpen) {
      setText(parseBackendTextToPlain(post.content));
      setBackendText(post.content);
      setSelectedTags(extractTagsFromBackendText(post.content));
      setSelectedMentions(extractMentionsFromBackendText(post.content));
      setExistingMedia(post.media || []);
      setPostType(post.type || PostType.GENERAL);
      // Initialize associated_with from post
      if (post.associated_with && Array.isArray(post.associated_with)) {
        const formattedAssociated: Mentions[] = post.associated_with.map(
          (item: any) => ({
            id: item.id,
            name: item.name || item.display_name || "",
            profile_picture:
              item.profile_picture || item.avatar
                ? {
                    url: item.profile_picture?.url || item.avatar || "",
                  }
                : undefined,
          })
        );
        setSelected(formattedAssociated);
      } else {
        setSelected([]);
      }
    }
  }, [
    isEditDialogOpen,
    post.content,
    post.media,
    post.type,
    post.associated_with,
  ]);

  // Keep selectedTags/selectedMentions in sync with text while editing
  useEffect(() => {
    if (!isEditDialogOpen) return;
    // Remove tags/mentions that are no longer in text
    const hashtagRegex = /#([a-zA-Z0-9_][a-zA-Z0-9_-]*)\b/g;
    const foundTags: string[] = [];
    let match;
    while ((match = hashtagRegex.exec(text)) !== null) {
      foundTags.push(match[1]);
    }
    setSelectedTags((prev) => prev.filter((t) => foundTags.includes(t.value)));
    const mentionRegex = /@([a-zA-Z0-9_]+)/g;
    const foundMentions: string[] = [];
    let m;
    while ((m = mentionRegex.exec(text)) !== null) {
      foundMentions.push(m[1]);
    }
    setSelectedMentions((prev) =>
      prev.filter((m) => foundMentions.includes(m.value))
    );
  }, [text, isEditDialogOpen]);

  const insertTextAtCursor = (textToInsert: string) => {
    if (!textAreaRef.current) return;

    const cursorPosition = textAreaRef.current.selectionStart;
    const textBefore = text.substring(0, cursorPosition);
    const textAfter = text.substring(cursorPosition);

    setText(textBefore + textToInsert + textAfter);

    // Move cursor to end of inserted text
    setTimeout(() => {
      if (textAreaRef.current) {
        textAreaRef.current.focus();
        const newPosition = cursorPosition + textToInsert.length;
        textAreaRef.current.setSelectionRange(newPosition, newPosition);
      }
    }, 0);
  };

  const removeExistingMedia = (mediaId: string) => {
    setExistingMedia((prev) => prev.filter((m) => m.id !== mediaId));
  };

  // Remove a single association by id
  const handleRemoveAssociation = (id: string) => {
    setSelected((prev) => prev.filter((item) => item.id !== id));
  };

  const handleDelete = async () => {
    setIsDropdownOpen(false);
    try {
      const variables = {
        input: {
          post_id: postId,
          delete: true,
        },
      };

      const { data } = await updatePost({ variables });

      if (data?.updatePost) {
        toast.success("Post deleted successfully");
        setIsDeleteDialogOpen(false);
        if (onPostUpdate) {
          onPostUpdate();
        }
      }
    } catch (error) {
      console.error("Error deleting post:", error);
      toast.error("Failed to delete post");
      setIsDeleteDialogOpen(false);
    }
  };

  const handleReport = async () => {
    setIsDropdownOpen(false);

    if (!user || !user.id) {
      toast.error("Please signup to report posts");
      return;
    }
    const variables = {
      reported_entity_id: postId,
    };

    try {
      const { data } = await addReport({ variables });
      if (data?.addReport) {
        const reportId = data.addReport.report_id;
        toast.success(
          `Your report has been sent successfully,\n it will be reviewed by team and necessary action will be taken \n your report id is ${reportId}`,
          { removeDelay: 4000 }
        );
      }
    } catch (error) {
      console.error("Error reporting post:", error);
      toast.error("Failed to report a post");
    }
  };

  const handleEdit = () => {
    setIsDropdownOpen(false);
    setIsEditDialogOpen(true);
  };

  const handleSubmit = async () => {
    if (!text.trim() && media.length === 0) {
      toast.error("Post cannot be empty");
      return;
    }

    setIsSubmitting(true);
    try {
      // Upload any new media
      const uploadedMedia = await uploadMedia();
      const newMediaIds = uploadedMedia.map((m) => m.id);
      const existingMediaIds = existingMedia.map((m) => m.id);
      const allMediaIds = [...existingMediaIds, ...newMediaIds];

      // 1. Find all hashtags in the text
      const hashtagRegex = /#([a-zA-Z0-9_][a-zA-Z0-9_-]*)\b/g;
      const foundTags: string[] = [];
      let match;
      while ((match = hashtagRegex.exec(text)) !== null) {
        foundTags.push(match[1]);
      }
      // 2. For each tag not in selectedTags, create it and add to selectedTags
      let newTags = [];
      for (const tag of foundTags) {
        if (
          !selectedTags.some((t) => t.value.toLowerCase() === tag.toLowerCase())
        ) {
          const { data } = await addTagMutation({
            variables: { tag_name: tag },
          });
          if (data?.addTag) {
            newTags.push({ id: data.addTag, value: tag });
          }
        }
      }
      let allTags = selectedTags;
      if (newTags.length > 0) {
        allTags = [...selectedTags, ...newTags];
        setSelectedTags(allTags);
      }
      // 3. Filter tags to only those present in the text
      const filteredTags = allTags.filter((t) => foundTags.includes(t.value));

      // Mentions
      const mentionRegex = /@([a-zA-Z0-9_]+)/g;
      const foundMentions: string[] = [];
      let m;
      while ((m = mentionRegex.exec(text)) !== null) {
        foundMentions.push(m[1]);
      }
      const filteredMentions = selectedMentions.filter((m) =>
        foundMentions.includes(m.value)
      );

      // Build the update input
      const variables = {
        input: {
          post_id: postId,
          type: postType,
          content: rebuildBackendText(text, filteredTags, filteredMentions),
          mentioned: filteredMentions.map((m) => m.id),
          tags: filteredTags.map((t) => t.id),
          links: links,
          media: allMediaIds,
          associated_with:
            postType === PostType.GENERAL ? [] : selected.map((m) => m.id),
        },
      };

      const { data } = await updatePost({ variables });

      if (data?.updatePost) {
        toast.success("Post updated successfully");
        setIsEditDialogOpen(false);
        // Refresh the page to reflect changes
        router.refresh();
        if (onPostUpdate) {
          onPostUpdate();
        }
      }
    } catch (error) {
      console.error("Error updating post:", error);
      toast.error("Failed to update post");
      setIsEditDialogOpen(false);
    } finally {
      setIsSubmitting(false);
    }
    sendClickEvent({
      category: "Button",
      action: "edit_post",
      label: "edit a post",
      value: 1,
      extraParams: {
        postId: postId,
      },
    });
  };

  return (
    <>
      <DropdownMenu open={isDropdownOpen} onOpenChange={setIsDropdownOpen}>
        <DropdownMenuTrigger className="p-2 hover:bg-secondary rounded-full">
          <MoreHorizontal size={18} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          {isAuthor ? (
            <>
              <DropdownMenuItem onClick={handleEdit}>
                <Edit2 size={16} className="mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-red-500 focus:text-red-500"
                onClick={() => {
                  setIsDeleteDialogOpen(true);
                  setIsDropdownOpen(false);
                }}
              >
                <Trash2 size={16} className="mr-2" />
                Delete
              </DropdownMenuItem>
              <DropdownMenuSeparator />
            </>
          ) : (
            <DropdownMenuItem
              onClick={handleReport}
              className="text-red-500 focus:text-red-500"
            >
              <Flag size={16} className="mr-2" />
              Report Post
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Delete Confirmation Dialog */}
      <Dialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Delete Post</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this post? This action cannot be
              undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="flex gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={() => {
                setIsDeleteDialogOpen(false);
                setIsDropdownOpen(false);
              }}
            >
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Edit Post</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Post type selector */}
            <div className="flex mb-2 overflow-x-auto hide-scrollbar">
              <div className="flex space-x-1 sm:space-x-2 text-xs sm:text-sm">
                <button
                  className={`inline-flex max-w-full items-center gap-2 px-3 py-1 border rounded-full transition
                    ${
                      postType === PostType.PROBLEM
                        ? theme === "dark"
                          ? "bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-gray-100 font-normal shadow-md border-red-700"
                          : "bg-gradient-to-r from-red-500 via-red-600 to-red-700 text-gray-800 font-normal shadow-md border-red-600"
                        : theme === "dark"
                          ? "border-gray-700 text-gray-200 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-red-800 md:hover:to-pink-800 md:hover:text-white"
                          : "border-gray-300 text-gray-800 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-red-700 md:hover:to-pink-700 md:hover:text-white"
                    }
                  `}
                  onClick={() =>
                    setPostType(
                      postType === PostType.PROBLEM
                        ? PostType.GENERAL
                        : PostType.PROBLEM
                    )
                  }
                >
                  <AlertTriangle className="w-4 h-4 mr-[-1px] z-50" />
                  Problem
                </button>
                <button
                  className={`inline-flex max-w-full items-center gap-2 px-3 py-1 border rounded-full transition
                    ${
                      postType === PostType.PRAISE
                        ? theme === "dark"
                          ? "bg-gradient-to-tr from-yellow-500 to-yellow-200 text-black font-normal shadow-md border-yellow-600"
                          : "bg-gradient-to-tr from-[#e0aa3e] to-[#f9f295] text-black font-normal shadow-md border-yellow-400"
                        : theme === "dark"
                          ? "border-gray-700 text-gray-200 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-yellow-800 md:hover:to-yellow-400 md:hover:text-black"
                          : "border-gray-300 text-gray-800 shadow hover:shadow-md md:hover:bg-gradient-to-r md:hover:from-[#e0aa3e] md:hover:to-[#f9f295] md:hover:text-primary"
                    }
                  `}
                  onClick={() =>
                    setPostType(
                      postType === PostType.PRAISE
                        ? PostType.GENERAL
                        : PostType.PRAISE
                    )
                  }
                >
                  <HandHeart className="w-4 h-4" /> Praise
                </button>
              </div>
            </div>
            {/* Associated with section */}
            <div className="flex mb-2 overflow-x-auto hide-scrollbar">
              <div className="flex space-x-1 sm:space-x-2 text-xs sm:text-sm">
                {postType !== PostType.GENERAL && (
                  <div className="flex items-center sm:h-6">
                    <AssociationWith
                      onSelect={setSelected}
                      selected={selected}
                      postType={postType}
                    />
                  </div>
                )}
                {postType !== PostType.GENERAL && selected.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        className="h-6 py-2 w-30 border rounded focus:outline-none relative inline-flex items-center gap-1 px-2 py-1 border rounded-full transition"
                      >
                        <div className="truncate w-10">{selected[0]?.name}</div>
                        <ChevronDown />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="w-56"
                      align="start"
                      side="bottom"
                      sideOffset={4}
                    >
                      <DropdownMenuLabel>Associated with</DropdownMenuLabel>
                      <DropdownMenuSeparator />
                      {selected.slice(0).map((item, index) => (
                        <DropdownMenuItem key={index}>
                          <div className="flex justify-between items-center w-full">
                            <div>{item.name}</div>
                            <button
                              className="w-4 h-4 flex items-center justify-center"
                              onClick={() => {
                                handleRemoveAssociation(item.id);
                              }}
                            >
                              <X />
                            </button>
                          </div>
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </div>
            </div>
            {/* Text input */}
            <div className="relative">
              <TextareaAutosize
                ref={textAreaRef}
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder="What's happening?"
                minRows={2}
                maxRows={12}
                className="w-full bg-transparent text-base sm:text-lg outline-none resize-none"
                maxLength={2000}
              />
              <div className="absolute left-0 right-0 z-50">
                <SuggestionSearch
                  text={text}
                  setText={setText}
                  backendText={backendText}
                  setBackendText={setBackendText}
                  textareaRef={textAreaRef}
                  onSelectMention={(mention) => {
                    setSelectedMentions((prev) => {
                      const newMentions = prev.some((m) => m.id === mention.id)
                        ? prev
                        : [...prev, mention];
                      return newMentions;
                    });
                  }}
                  onSelectTag={(tag) => {
                    setSelectedTags((prev) => {
                      const newTags = prev.some((t) => t.id === tag.id)
                        ? prev
                        : [...prev, tag];
                      return newTags;
                    });
                  }}
                />
              </div>
            </div>

            {/* Character count */}
            <div
              className={`text-xs text-right ${charCount > 2000 * 0.9 ? "text-destructive" : "text-muted-foreground"}`}
            >
              {charCount}/2000
            </div>

            {/* Link previews */}
            {links.length > 0 && (
              <div className="mt-2 space-y-2 max-w-full overflow-hidden">
                {links.map((link, index) => (
                  <LinkPreview key={index} url={link} />
                ))}
              </div>
            )}

            {/* Media preview */}
            {(existingMedia.length > 0 || mediaPreview.length > 0) && (
              <div
                className={`mt-2 grid gap-1 sm:gap-2 ${
                  existingMedia.length + mediaPreview.length === 1
                    ? "grid-cols-1"
                    : "grid-cols-2"
                }`}
              >
                <Carousel>
                  <CarouselContent>
                    {existingMedia.map((mediaItem, index) => (
                      <CarouselItem key={mediaItem.id}>
                        <div
                          key={mediaItem.id}
                          className="relative rounded-md overflow-hidden w-full"
                        >
                          {mediaItem.type.startsWith("video/") ? (
                            <video
                              src={mediaItem.url}
                              controls
                              className="w-full h-36 sm:h-48 object-cover"
                            />
                          ) : (
                            <Image
                              src={mediaItem.url}
                              alt={`Preview ${index}`}
                              width={300}
                              height={300}
                              className="object-cover w-full h-36 sm:h-48"
                            />
                          )}
                          <button
                            className="absolute top-2 right-2 bg-black bg-opacity-50 rounded-full p-1 hover:bg-opacity-70"
                            onClick={() => removeExistingMedia(mediaItem.id)}
                          >
                            <X size={16} className="text-white" />
                          </button>
                        </div>
                      </CarouselItem>
                    ))}
                    {mediaPreview.map((previewUrl, index) => (
                      <CarouselItem key={index}>
                        <div
                          key={index}
                          className="relative rounded-md overflow-hidden w-full"
                        >
                          {media[index].type.startsWith("video/") ? (
                            <video
                              src={previewUrl}
                              controls
                              className="w-full h-36 sm:h-48 object-cover"
                            />
                          ) : (
                            <Image
                              src={previewUrl}
                              alt={`Preview ${index}`}
                              width={300}
                              height={300}
                              className="object-cover w-full h-36 sm:h-48"
                            />
                          )}
                          <button
                            className="absolute top-2 right-2 bg-black bg-opacity-50 rounded-full p-1 hover:bg-opacity-70"
                            onClick={() => removeMedia(index)}
                          >
                            <X size={16} className="text-white" />
                          </button>
                        </div>
                      </CarouselItem>
                    ))}
                  </CarouselContent>
                  {existingMedia.length + mediaPreview.length > 1 && (
                    <>
                      <CarouselPrevious className="absolute left-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white disabled:hidden" />
                      <CarouselNext className="absolute right-2 top-1/2 -translate-y-1/2 z-20 bg-black/50 p-2 rounded-full text-white disabled:hidden" />
                    </>
                  )}
                </Carousel>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-wrap justify-between items-center mt-2 sm:mt-3">
              <div className="flex flex-wrap -ml-1">
                {/* Media upload button */}
                <button
                  className="text-primary hover:text-primary/80 p-1.5 sm:p-2 rounded-full hover:bg-secondary"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={existingMedia.length + media.length >= 4}
                  title={
                    existingMedia.length + media.length >= 4
                      ? "Maximum media limit reached"
                      : "Add photos or video"
                  }
                >
                  <ImageIcon size={20} />
                </button>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleMediaUpload}
                  className="hidden"
                  accept="image/*,video/*"
                  multiple
                />

                {/* Link button */}
                <button
                  className="text-primary hover:text-primary/80 p-1.5 sm:p-2 rounded-full hover:bg-secondary"
                  onClick={() => {
                    const url = prompt("Enter URL:");
                    if (url && url.trim()) {
                      insertTextAtCursor(` ${url} `);
                    }
                  }}
                >
                  <LinkIcon size={20} />
                </button>

                {/* Emoji button */}
                <div className="relative" ref={pickerRef}>
                  <button
                    className="text-primary hover:text-primary/80 p-1.5 sm:p-2 rounded-full hover:bg-secondary"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                  >
                    <Smile size={20} />
                  </button>

                  {showEmojiPicker && (
                    <div
                      className="absolute z-10 mt-1 bg-background border border-border rounded-md shadow-lg
                                left-1/2 -translate-x-1/2 sm:left-0 sm:translate-x-0"
                      style={{
                        width: "90vw",
                        maxWidth: "320px",
                      }}
                    >
                      <Picker
                        data={data}
                        onEmojiSelect={(emoji: any) =>
                          handleEmojiSelect(emoji.native, insertTextAtCursor)
                        }
                        theme="light"
                        previewPosition="none"
                        emojiSize={20}
                        maxFrequentRows={3}
                      />
                    </div>
                  )}
                </div>

                {/* Hashtag and Mention buttons */}
                <button
                  className="text-primary hover:text-primary/80 p-1.5 sm:p-2 rounded-full hover:bg-secondary"
                  onClick={() => insertTextAtCursor("#")}
                >
                  <Hash size={20} />
                </button>
                <button
                  className="text-primary hover:text-primary/80 p-1.5 sm:p-2 rounded-full hover:bg-secondary"
                  onClick={() => insertTextAtCursor("@")}
                >
                  <AtSign size={20} />
                </button>
              </div>

              {/* Save button */}
              <Button
                onClick={handleSubmit}
                disabled={
                  isSubmitting ||
                  (text.trim() === "" &&
                    media.length === 0 &&
                    existingMedia.length === 0) ||
                  charCount > 2000
                }
                className={`mt-2 sm:mt-0 px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-bold text-sm ${
                  isSubmitting ||
                  (text.trim() === "" &&
                    media.length === 0 &&
                    existingMedia.length === 0) ||
                  charCount > 2000
                    ? "bg-primary/50 text-primary-foreground/50 cursor-not-allowed"
                    : "bg-primary text-primary-foreground hover:bg-primary/90"
                }`}
              >
                {isSubmitting ? "Saving..." : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PostMenu;
